import { formatDateToString, DATE_FORMAT } from "helpers/dates"
import { hasDefinedValue } from "components/charts/helpers/has-defined-value"
import { warn } from "helpers/dev-logger"
import { DESC } from "helpers/sorts"

// Shared by getLatestItemValues/Dates/BatchIds: the latest row with a defined value, not necessarily the last row.
function findLatestDefinedRowIndex(data, itemId) {
  for (let index = data.length - 1; index >= 0; index -= 1) {
    const value = data[index]?.[itemId]
    if (hasDefinedValue(value)) {
      return index
    }
  }
  return -1
}

// Latest defined value per item id; not necessarily the last row, since a series can end earlier than others.
export function getLatestItemValues(data, items) {
  // A dataTransform can return nothing while status is still OK, ahead of index.jsx's empty-data guard.
  if (!Array.isArray(data)) return {}

  const latestValues = {}

  items.forEach(item => {
    const index = findLatestDefinedRowIndex(data, item.id)
    if (index !== -1) {
      latestValues[item.id] = data[index][item.id]
    }
  })

  return latestValues
}

// Latest known date per item id (YYYY-MM-DD), from the same row getLatestItemValues reads its value from.
export function getLatestItemDates(data, items) {
  // Same dataTransform/OK-status gap as getLatestItemValues above — see its comment.
  if (!Array.isArray(data)) return {}

  const latestDates = {}

  items.forEach(item => {
    const index = findLatestDefinedRowIndex(data, item.id)
    if (index === -1) return
    // Every row is stamped with `date` by contract; a missing one is a gatherer bug worth surfacing.
    const date = data[index]?.date
    if (date) {
      latestDates[item.id] = formatDateToString(date, DATE_FORMAT)
    } else {
      warn(`getLatestItemDates: row at index ${index} for item "${item.id}" has no date`)
    }
  })

  return latestDates
}

// Latest batchId per item id, from the same row getLatestItemDates reads its date from; omitted (not undefined) when that row has none.
export function getLatestItemBatchIds(data, items) {
  if (!Array.isArray(data)) return {}

  const latestBatchIds = {}

  items.forEach(item => {
    const index = findLatestDefinedRowIndex(data, item.id)
    if (index === -1) return
    const batchId = data[index]?.batchId
    if (batchId) {
      latestBatchIds[item.id] = batchId
    }
  })

  return latestBatchIds
}

export const LEGEND_SORT_COLUMNS = { NAME: "name", VALUE: "value" }

/** Returns a sorted copy for display only; an item with no latest value always sorts last. */
export function sortLegendItems(items, latestValues, sortColumn, sortDirection, isAbsolute = false) {
  const directionMultiplier = sortDirection === DESC ? -1 : 1
  const byLabelAscending = (a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })

  if (sortColumn === LEGEND_SORT_COLUMNS.VALUE) {
    return [...items].sort((a, b) => {
      const aRawValue = latestValues[a.id]
      const bRawValue = latestValues[b.id]
      const aHasValue = hasDefinedValue(aRawValue)
      const bHasValue = hasDefinedValue(bRawValue)
      if (aHasValue !== bHasValue) return aHasValue ? -1 : 1
      if (!aHasValue) return byLabelAscending(a, b)
      const aValue = isAbsolute ? Math.abs(aRawValue) : aRawValue
      const bValue = isAbsolute ? Math.abs(bRawValue) : bRawValue
      return aValue === bValue ? byLabelAscending(a, b) : (aValue - bValue) * directionMultiplier
    })
  }

  return [...items].sort((a, b) => byLabelAscending(a, b) * directionMultiplier)
}
